import { SharedIcon, SharedIconProps } from 'components/SharedIcon/SharedIcon';
import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import RoleTag from './RoleTag';
import { MaterialSymbols } from 'material-design-icons-literal-types';
import { FormExportButton } from 'components/IAP/FormExportButton';

type HeaderLeftProps = {
  iconName: MaterialSymbols;
  iconColor: SharedIconProps['color'];
  title: string;
  subtitle?: string;
  titleInfoTooltip?: string;
};

const HeaderLeft: React.FC<HeaderLeftProps> = ({
  iconName,
  iconColor,
  title,
  subtitle,
  titleInfoTooltip,
}) => {
  return (
    <div className="d-flex flex-column align-items-start">
      <h3 className="d-flex align-items-center mt-0 mb-0 flex-wrap gap-1">
        <SharedIcon iconName={iconName} classes="me-2" color={iconColor} />
        <span>{title}</span>
        {titleInfoTooltip ? (
          <OverlayTrigger
            placement="top"
            overlay={(props) => (
              <Tooltip id="ics-widget-header-info-tooltip" {...props}>
                {titleInfoTooltip}
              </Tooltip>
            )}
          >
            <span
              className="d-inline-flex align-items-center text-weak"
              role="button"
              tabIndex={0}
              aria-label={titleInfoTooltip}
            >
              <SharedIcon iconName="info" size="sm" color="primary" />
            </span>
          </OverlayTrigger>
        ) : null}
      </h3>
      {subtitle ? <div className="text-weak ps-4">{subtitle}</div> : null}
    </div>
  );
};

type HeaderRightProps = {
  icsRoles: string[];
  handleExport?: (format: 'pdf' | 'docx') => void;
  exportText?: string;
  disabledExport?: boolean;
  /**
   * Form this button exports. When the incident's authority does not publish
   * it, the button is disabled with an explanation rather than failing on
   * click. It also decides whether Word is offered alongside PDF.
   */
  exportFormname?: string;
};

const HeaderRight: React.FC<HeaderRightProps> = ({
  icsRoles,
  handleExport,
  exportText,
  disabledExport,
  exportFormname,
}) => {
  return (
    <div className="d-flex align-items-center gap-2">
      <RoleTag icsRoles={icsRoles} />
      {handleExport && exportFormname ? (
        <FormExportButton
          formname={exportFormname}
          onExport={handleExport}
          label={exportText}
          disabled={disabledExport}
        />
      ) : null}
    </div>
  );
};

export const ICSWidget = {
  HeaderLeft,
  HeaderRight,
};

export default ICSWidget;
